import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UserToShareI } from 'api/sharing/sharing';

interface ProjectI {
  projectId: string;
  name: string;
  description: string;
  createdAt: string;
  createdByUser: { userId: string; username: string; email: string };
  sharedWithUsers: { userId: string; username: string; email: string }[];
  totalWorkflows: number;
}

interface WorkspaceState {
  projects: ProjectI[];
  checkedProjects: string[];
  openAddToProjectModal: boolean;
  openCreateNewProjectModal: boolean;
  openShareProjectModal: boolean;
  openSuccessSharingModal: boolean;
  openSuccessPublishingModal: boolean;
  selectForWorkspaceMode: boolean;
  checkedPatents: string[];
  newProjectName: string;
  newProjectDescription: string;
  createdByUser: {
    email: string;
    userId: string;
    username: string;
  } | null;
  userPermissions: UserToShareI[];
}

const initialState: WorkspaceState = {
  projects: [],
  checkedProjects: [],
  openAddToProjectModal: false,
  openCreateNewProjectModal: false,
  openShareProjectModal: false,
  openSuccessSharingModal: false,
  openSuccessPublishingModal: false,
  selectForWorkspaceMode: false,
  checkedPatents: [],
  newProjectName: '',
  newProjectDescription: '',
  createdByUser: null,
  userPermissions: [],
};

const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    addProject: (state, action: PayloadAction<ProjectI>) => {
      state.projects.push(action.payload);
    },
    setInvDiscUsersInfo: (
      state,
      action: PayloadAction<{
        createdByUser: {
          email: string;
          userId: string;
          username: string;
        } | null;
        userPermissions: UserToShareI[];
      }>,
    ) => {
      state.createdByUser = action.payload.createdByUser;
      state.userPermissions = action.payload.userPermissions;
    },
    setSelectForWorkspaceMode: (state, action: PayloadAction<boolean>) => {
      state.selectForWorkspaceMode = action.payload;
    },
    checkProject: (state, action: PayloadAction<string>) => {
      state.checkedProjects = state.checkedProjects.includes(action.payload)
        ? state.checkedProjects.filter(v => v !== action.payload)
        : [...state.checkedProjects, action.payload];
    },
    checkAllProjects: (state, action: PayloadAction<string[]>) => {
      state.checkedProjects = action.payload;
    },
    handleAddToProjectModal: (state, action: PayloadAction<boolean>) => {
      state.openAddToProjectModal = action.payload;
    },
    handleCreateNewProjectModal: (state, action: PayloadAction<boolean>) => {
      state.openCreateNewProjectModal = action.payload;
    },
    handleShareProjectModal: (state, action: PayloadAction<boolean>) => {
      state.openShareProjectModal = action.payload;
    },
    handleSuccessSharingModal: (state, action: PayloadAction<boolean>) => {
      state.openSuccessSharingModal = action.payload;
    },
    handleSuccessPublishingModal: (state, action: PayloadAction<boolean>) => {
      state.openSuccessPublishingModal = action.payload;
    },
    checkPatentForWorkspace: (state, action: PayloadAction<string>) => {
      state.checkedPatents = state.checkedPatents.includes(action.payload)
        ? state.checkedPatents.filter(v => v !== action.payload)
        : [...state.checkedPatents, action.payload];
    },
    removeCheckedPatentsForWorkspace: state => {
      state.checkedPatents = [];
    },
    setNewProjectName: (state, action: PayloadAction<string>) => {
      state.newProjectName = action.payload;
    },
    setNewProjectDescription: (state, action: PayloadAction<string>) => {
      state.newProjectDescription = action.payload;
    },
    resetWorkspaceSlice: () => initialState,
  },
});

export default workspaceSlice.reducer;

// Action creators are generated for each case reducer function
export const { checkPatentForWorkspace } = workspaceSlice.actions;
